import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X, Check, Clock, AlertTriangle, Calendar, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import apiClient from '../api';  // R22 E9: Use apiClient for auto token refresh

// Format date relative to now
const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `In ${diffDays} days`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Priority badge
const PriorityBadge = ({ priority, daysUntilDue }) => {
  let color = 'bg-slate-100 text-slate-600';
  let label = 'Normal';
  
  if (daysUntilDue < 0) {
    color = 'bg-rose-100 text-rose-700';
    label = 'Overdue';
  } else if (daysUntilDue <= 2) {
    color = 'bg-rose-100 text-rose-700';
    label = 'Urgent';
  } else if (daysUntilDue <= 7) {
    color = 'bg-amber-100 text-amber-700';
    label = 'Soon';
  }
  
  return <Badge className={`${color} text-xs`}>{label}</Badge>;
};

// Notification item
const NotificationItem = ({ item, onDismiss, onComplete }) => {
  const dueDate = new Date(item.due_date);
  const now = new Date();
  const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
  
  return (
    <div 
      className={`p-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors ${
        daysUntilDue < 0 ? 'bg-rose-50' : daysUntilDue <= 2 ? 'bg-amber-50' : ''
      }`}
      data-testid={`notification-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          daysUntilDue < 0 ? 'bg-rose-100' : daysUntilDue <= 2 ? 'bg-amber-100' : 'bg-blue-100'
        }`}>
          {daysUntilDue < 0 ? (
            <AlertTriangle className="w-4 h-4 text-rose-600" />
          ) : (
            <Calendar className="w-4 h-4 text-blue-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm text-slate-900 truncate">{item.name}</span>
            <PriorityBadge daysUntilDue={daysUntilDue} />
          </div>
          <p className="text-xs text-slate-500 mb-2">{item.description}</p>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Clock className="w-3 h-3" />
            <span>{formatRelativeDate(item.due_date)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!item.completed && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0 text-emerald-600"
              onClick={() => onComplete(item.id)}
              title="Mark complete"
            >
              <Check className="w-4 h-4" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0 text-slate-400"
            onClick={() => onDismiss(item.id)}
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// PC149(b) 2026-09-15: Persist dismissed IDs to localStorage so notifications
// actually stay dismissed across page reloads / logouts. Prior behavior kept
// dismissedIds in React state only, so every mount re-fetched and re-showed
// the same items even after the user X'd them out. Keyed per browser; a full
// backend-side dismiss table would be nicer (multi-device) but is a follow-up.
const DISMISSED_KEY = 'notification_dismissed_ids';
const _loadDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};
const _saveDismissed = (ids) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch (_) {
    // localStorage may be disabled / full; fail silently, dismiss will still
    // work within the current session.
  }
};

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(_loadDismissed);

  // PC149(c) 2026-09-26: dismissals also live on the server so they follow the
  // user to other devices. Merge server list into local on mount; push any
  // local-only ids (dismissed before this release) up once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/user/dismissed-notifications');
        const server = (res.data?.dismissed_ids || []).map(String);
        if (cancelled) return;
        const local = _loadDismissed().map(String);
        const merged = Array.from(new Set([...local, ...server]));
        const localOnly = local.filter(id => !server.includes(id));
        if (localOnly.length) {
          apiClient.post('/user/dismissed-notifications', { ids: localOnly }).catch(() => {});
        }
        _saveDismissed(merged);
        setDismissedIds(merged);
      } catch (_) {
        // offline / older backend: keep localStorage behavior
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const _pushDismissed = (id) => {
    apiClient.post('/user/dismissed-notifications', { ids: [String(id)] }).catch(() => {});
  };

  // R22 E9: Removed getAuthHeaders - apiClient handles auth automatically

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const taxYear = new Date().getFullYear();
      // R22 E9: Use apiClient instead of raw fetch for auto token refresh
      const response = await apiClient.get(`/v2/collector/timeline/${taxYear}`);
      const data = response.data;
      
      // Filter for upcoming or overdue items only
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const relevantItems = (data.items || data || []).filter(item => {
        if (item.completed) return false;
        const dueDate = new Date(item.due_date);
        return dueDate <= thirtyDaysFromNow;
      }).filter(item => !dismissedIds.includes(item.id));
      
      // Sort by due date
      relevantItems.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      
      setNotifications(relevantItems);
    } catch (err) {
      // R22 E9: Silently fail for 401/403 - user may not be logged in or doesn't have collector role
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        console.error('Failed to fetch notifications:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [dismissedIds]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleDismiss = (id) => {
    // PC149(b): persist across reloads. Backend has no dismiss endpoint yet;
    // localStorage is the pragmatic single-browser fix for now.
    setDismissedIds(prev => {
      const next = [...prev, id];
      _saveDismissed(next);
      return next;
    });
    _pushDismissed(id);  // PC149(c): cross-device
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleComplete = async (id) => {
    try {
      // R22 E9: Use apiClient instead of raw fetch
      await apiClient.put(`/v2/collector/timeline/item/${id}/complete`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Task marked as complete');
    } catch (err) {
      console.error('Failed to complete task:', err);
      // Still remove from list for better UX
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
    // PC149(b): also record as dismissed so if the backend mark fails silently,
    // the user still sees it stay dismissed across reloads.
    setDismissedIds(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      _saveDismissed(next);
      return next;
    });
    _pushDismissed(id);  // PC149(c): cross-device
  };

  const unreadCount = notifications.filter(n => {
    const dueDate = new Date(n.due_date);
    const daysUntilDue = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 7;
  }).length;

  return (
    <div className="relative" data-testid="notification-center">
      {/* Bell Icon Button */}
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown Panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-lg border z-50" data-testid="notification-panel">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-slate-900">Notifications</h3>
              <span className="text-xs text-slate-500">
                {notifications.length} upcoming
              </span>
            </div>

            {/* Content */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <Clock className="w-8 h-8 mx-auto text-slate-300 animate-pulse" />
                  <p className="text-sm text-slate-500 mt-2">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-8 h-8 mx-auto text-slate-300" />
                  <p className="text-sm text-slate-500 mt-2">No upcoming deadlines</p>
                  <p className="text-xs text-slate-400">Check back later for new tasks</p>
                </div>
              ) : (
                notifications.map(item => (
                  <NotificationItem
                    key={item.id}
                    item={item}
                    onDismiss={handleDismiss}
                    onComplete={handleComplete}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-blue-600"
                  onClick={() => {
                    setOpen(false);
                    window.location.href = '/collector/settings';
                  }}
                >
                  View All Tasks <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
